import React, { useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { errMsg } from "../../lib/errMsg";
import { type RebootResult, rebootBox } from "../../services/boxDetailApi";

/**
 * Restart the SCU -- the compute unit, and ONLY that.
 *
 * NOT the SmartBox. There is no way to power-cycle the container itself from
 * here, and a label implying otherwise would send an operator looking for a
 * button that does not exist -- or worse, leave them believing a wedged box
 * had been fully reset when only its computer had. Every string in this card
 * says SCU on purpose.
 *
 * The most disruptive control in the app, so it is built to be hard to press
 * by accident and honest about what it did:
 *
 *  - Two steps. The first click arms it and names the consequence; nothing is
 *    sent until the second. A single misplaced click cannot take a rig's SCU
 *    off the air.
 *  - It reports SCHEDULED, not recovered. The API answers as soon as the SCU
 *    accepts the command, which is seconds, while it is away for a minute or
 *    more. Showing a green tick at that moment would say the
 *    opposite of what is true, so the result says when to expect it back and
 *    the card stays in that state rather than resetting.
 *  - The server enforces all of this independently (`operator.triage`, write
 *    scope on the SCU, the `AllowScuReboot` kill switch, and `confirm`). The
 *    gate here only decides whether to draw the control.
 */
export const RebootCard: React.FC<{ scuName: string }> = ({ scuName }) => {
  const { token, can } = useAuth();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RebootResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Drawn only for operators. A viewer seeing a disabled button would just be
  // an invitation to ask why it is disabled.
  if (!can("operator.triage")) return null;

  const go = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await rebootBox(scuName, token));
      setArmed(false);
    } catch (e) {
      setError(errMsg(e));
      setArmed(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-secondary-200 p-4">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-medium text-secondary-900">Restart SCU</h3>
        <span className="text-[11px] text-secondary-400">over SSH</span>
      </div>

      <p className="text-xs text-secondary-500 mb-3">
        Reboots the <strong>SCU</strong> — the computer — not the scanner
        service and not the SmartBox itself, which cannot be power-cycled from
        here. The SCU stops scanning and leaves the network until it comes
        back. If the problem is the reader, try the reader&apos;s own controls
        first.
      </p>

      {result ? (
        <div className="text-sm">
          <div className="text-success-700 font-medium">Reboot sent</div>
          <div className="text-xs text-secondary-600 mt-1">{result.note}</div>
          <div className="text-xs text-secondary-500 mt-2">
            {/* Deliberately not a spinner or a tick: we know when the SCU was
                ASKED, and nothing here knows when it returns. */}
            Expect the SCU back in roughly{" "}
            {Math.round(result.expect_back_after_seconds / 60)} minute
            {result.expect_back_after_seconds >= 120 ? "s" : ""}. This page will
            not know until it reports in again.
          </div>
        </div>
      ) : armed ? (
        <div>
          <div className="text-sm text-error-700 mb-2">
            Restart the SCU <span className="font-mono">{scuName}</span> now?
            It will stop scanning and be unreachable for a minute or more.
            The SmartBox itself is not affected — only its computer restarts.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={go}
              disabled={busy}
              className="px-3 py-1.5 rounded text-sm text-white bg-error-600 hover:bg-error-700 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Yes, restart it"}
            </button>
            <button
              type="button"
              onClick={() => setArmed(false)}
              disabled={busy}
              className="px-3 py-1.5 rounded text-sm border border-secondary-300 hover:bg-secondary-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setArmed(true)}
          className="px-3 py-1.5 rounded text-sm border border-error-300 text-error-700 hover:bg-error-50"
        >
          Restart this SCU…
        </button>
      )}

      {error && (
        <div className="mt-3 text-xs text-error-700 bg-error-50 border border-error-200 px-2 py-1.5 rounded">
          {error}
        </div>
      )}
    </div>
  );
};

export default RebootCard;
